import type { BuilderState } from "../builder-state";
import { NO_CAPS, type ConnectionString, type DialectCapabilities } from "./shared";

export const label = "MongoDB";
export const defaultPort = 27017;

export const caps: DialectCapabilities = {
  ...NO_CAPS,
  superuserToggle: true, // root role
  connectionLimit: true,
  validUntil: true,
};

export function generate(s: BuilderState): string {
  const out: string[] = [];
  const push = (l = "") => out.push(l);
  const c = (t: string) => s.includeComments && push(`// ${t}`);

  const jsStr = (v: string) => JSON.stringify(v);
  const dbName = s.dbName || "app_db";
  const userName = s.userName || "app_user";
  const pwd = s.password || "CHANGE_ME";

  // Map relational privileges to Mongo built-in roles.
  const roles: string[] = [];
  const writes = s.privInsert || s.privUpdate || s.privDelete || s.privTruncate;
  if (s.superuser) {
    roles.push("root");
  } else if (s.privCreateSchema || s.createSchema) {
    roles.push("dbOwner");
  } else if (writes) {
    roles.push("readWrite");
  } else if (s.privSelect) {
    roles.push("read");
  }
  if (s.createDb) roles.push("dbAdmin");
  if (s.createRole) roles.push("userAdmin");
  if (roles.length === 0) roles.push("read");

  c("============================================================");
  c(`MongoDB — ${s.mode === "create" ? "Create" : "Update"} user`);
  c("Run with mongosh, connected as a userAdmin (or root) on the admin DB.");
  c("MongoDB has no SQL: this script uses the shell command API.");
  c("============================================================");
  push();

  c("Authentication must be enabled in mongod.conf:");
  c("  security:");
  c("    authorization: enabled");
  push();

  push(`use ${jsStr(dbName)};`);
  push();

  const rolesArr = roles.map((r) => `{ role: ${jsStr(r)}, db: ${jsStr(dbName)} }`).join(", ");

  if (s.mode === "create") {
    c("---- 1. CREATE USER -----------------------------------------");
    c("Mongo creates the database lazily on first write. The user is");
    c("stored in the DB you switched to (here: the target DB).");
    push(`db.createUser({`);
    push(`  user: ${jsStr(userName)},`);
    push(`  pwd: ${jsStr(pwd)},`);
    push(`  roles: [ ${rolesArr} ]`);
    push(`});`);
    push();
  } else {
    c("---- 1. UPDATE USER -----------------------------------------");
    if (s.resetPassword && s.password) {
      push(`db.changeUserPassword(${jsStr(userName)}, ${jsStr(pwd)});`);
    }
    if (s.revokeBeforeGrant) {
      c("Revoke all current roles on this DB before re-granting.");
      push(`db.revokeRolesFromUser(${jsStr(userName)}, db.getUser(${jsStr(userName)}).roles);`);
    }
    push(`db.grantRolesToUser(${jsStr(userName)}, [ ${rolesArr} ]);`);
    push();
  }

  if (s.connectionLimit !== -1) {
    c(
      `Connection limits are set per-deployment via net.maxIncomingConnections (${s.connectionLimit}).`,
    );
    push();
  }
  if (s.validUntil) {
    c(`Mongo has no native password expiry. Track ${s.validUntil} externally.`);
    push();
  }

  return out.join("\n");
}

export function connectionStrings(s: BuilderState): ConnectionString[] {
  const pwd = encodeURIComponent(s.password);
  const user = encodeURIComponent(s.userName);
  const dbSeg = encodeURIComponent(s.dbName);
  const auth = pwd ? `${user}:${pwd}` : user;
  return [
    {
      label: "MongoDB URI (mongosh / drivers)",
      url: `mongodb://${auth}@${s.host}:${s.port}/${dbSeg}?authSource=${dbSeg}`,
      note: "authSource is the DB where the user was created.",
    },
    {
      label: "MongoDB SRV (Atlas / replica set)",
      url: `mongodb+srv://${auth}@${s.host}/${dbSeg}?retryWrites=true&w=majority`,
    },
    {
      label: "PyMongo / Motor",
      url: `mongodb://${auth}@${s.host}:${s.port}/${dbSeg}?authSource=${dbSeg}`,
    },
  ];
}
